/**
 * CohortDashboard — Cohort Admin / Member View
 * ==============================================
 * Route: /cohort/:slug
 *
 * Features:
 *   - Project grid/list view with taxonomy label filters
 *   - Copy join link button (cohort admins)
 *   - Inline settings panels: instructor controls, taxonomy editor, visibility
 *   - Member list with remove button (cohort admins)
 *   - Instructor controls: lock toggle, expiration date, capacity cap
 *
 * Place at: frontend/src/pages/CohortDashboard.jsx
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Users, Settings, Grid3X3, List, Copy, Check, Plus, X,
  Tag, Eye, EyeOff, Globe, Lock, Building2, Loader2,
  ChevronDown, ChevronUp, Sparkles, ExternalLink, Trash2,
  Calendar, Hash, Unlock, AlertCircle, Clock, Map as MapIcon,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import CosmicMap from '../components/map/CosmicMap';
import useMapController from '../hooks/useMapController';
import ConnectionInfoCard from '../components/map/ConnectionInfoCard';
import MapChrome from '../components/map/MapChrome';
import MapLegend from '../components/map/MapLegend';
import LineageScrubber from '../components/journey/LineageScrubber';
import CreateProjectOverlay from '../components/overlays/CreateProjectOverlay';
import ProjectDetailOverlay from '../components/overlays/ProjectDetailOverlay';
import OverlayErrorBoundary from '../components/overlays/OverlayErrorBoundary';

// ── Zone colors for project cards ──────────────────────────────────────
const ZONE_COLORS = {
  build:     '#f59e0b',
  beautiful: '#ec4899',
  sound:     '#06b6d4',
  taste:     '#f97316',
  story:     '#8b5cf6',
  heal:      '#ef4444',
  smart:     '#6366f1',
  sell:      '#22c55e',
};

const VISIBILITY_ICONS = {
  public:  Globe,
  private: Lock,
  campus:  Building2,
};

const VISIBILITY_LABELS = {
  public:  'Public — visible on the global map',
  private: 'Private — cohort members only',
  campus:  'Campus — visible to campus members',
};


// ========================================================================
// PROJECT CARD
// ========================================================================

function ProjectCard({ project, viewMode, onOpen }) {
  const zoneColor = ZONE_COLORS[project.category] || '#71717a';
  const labels = project.cohortLabels || [];

  if (viewMode === 'list') {
    return (
      <div
        onClick={() => onOpen?.(project)}
        className="flex items-center gap-4 p-4 bg-zinc-900/50 hover:bg-zinc-800/50 border border-zinc-800 rounded-xl cursor-pointer transition-colors"
      >
        {/* Thumbnail */}
        <div
          className="w-12 h-12 rounded-lg bg-zinc-800 flex-shrink-0 flex items-center justify-center overflow-hidden"
          style={{ borderLeft: `3px solid ${zoneColor}` }}
        >
          {project.image_url || project.imageUrl ? (
            <img
              src={project.image_url || project.imageUrl}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-lg font-bold text-zinc-600">
              {(project.name || '?')[0].toUpperCase()}
            </span>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-white truncate">{project.name}</h3>
          <p className="text-xs text-zinc-500 truncate">
            {project.author?.username || 'Unknown'} · {project.project_type || project.projectType || 'digital'}
          </p>
        </div>

        {/* Labels */}
        {labels.length > 0 && (
          <div className="flex gap-1.5 flex-shrink-0">
            {labels.slice(0, 2).map(l => (
              <span
                key={l.id}
                className="px-2 py-0.5 rounded text-[10px] font-medium bg-purple-500/15 text-purple-300"
              >
                {l.label}
              </span>
            ))}
            {labels.length > 2 && (
              <span className="px-1.5 py-0.5 rounded text-[10px] text-zinc-500">
                +{labels.length - 2}
              </span>
            )}
          </div>
        )}

        <ExternalLink className="w-4 h-4 text-zinc-600 flex-shrink-0" />
      </div>
    );
  }

  // Grid view
  return (
    <div
      onClick={() => onOpen?.(project)}
      className="group bg-zinc-900/50 hover:bg-zinc-800/50 border border-zinc-800 rounded-xl overflow-hidden cursor-pointer transition-colors"
    >
      {/* Cover image */}
      <div className="aspect-video bg-zinc-800 relative overflow-hidden">
        {project.image_url || project.imageUrl ? (
          <img
            src={project.image_url || project.imageUrl}
            alt=""
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-3xl font-bold text-zinc-700">
              {(project.name || '?')[0].toUpperCase()}
            </span>
          </div>
        )}
        {/* Zone indicator */}
        <div
          className="absolute top-2 right-2 w-3 h-3 rounded-full border-2 border-zinc-900"
          style={{ backgroundColor: zoneColor }}
        />
      </div>

      {/* Info */}
      <div className="p-3">
        <h3 className="text-sm font-medium text-white truncate mb-1">{project.name}</h3>
        <p className="text-xs text-zinc-500 mb-2">
          {project.author?.username || 'Unknown'}
        </p>
        {labels.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {labels.map(l => (
              <span
                key={l.id}
                className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-500/15 text-purple-300"
              >
                {l.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ========================================================================
// INSTRUCTOR CONTROLS (lock, expiration, capacity)
// ========================================================================

function InstructorControls({ cohort, onUpdate }) {
  const [isLocked, setIsLocked] = useState(cohort.isLocked || false);
  const [expiresAt, setExpiresAt] = useState(
    cohort.expiresAt ? cohort.expiresAt.slice(0, 16) : '' // format for datetime-local input
  );
  const [maxMembers, setMaxMembers] = useState(cohort.maxMembers ?? '');
  const [saving, setSaving] = useState(false);

  // Sync with prop changes (e.g. after parent refetch)
  useEffect(() => {
    setIsLocked(cohort.isLocked || false);
    setExpiresAt(cohort.expiresAt ? cohort.expiresAt.slice(0, 16) : '');
    setMaxMembers(cohort.maxMembers ?? '');
  }, [cohort.isLocked, cohort.expiresAt, cohort.maxMembers]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/cohorts/${cohort.slug}`, {
        isLocked,
        expiresAt: expiresAt || null,
        maxMembers: maxMembers === '' ? null : parseInt(maxMembers, 10),
      });
      onUpdate();
      toast.success('Class settings saved');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const isExpired = expiresAt && new Date(expiresAt) < new Date();
  const isFull = cohort.maxMembers && cohort.studentCount >= cohort.maxMembers;

  return (
    <div className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl space-y-5">
      <h3 className="text-sm font-medium text-white flex items-center gap-2">
        <Settings className="w-4 h-4 text-amber-400" />
        Class Controls
      </h3>

      {/* Status summary */}
      <div className="flex flex-wrap gap-2">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${
          isLocked
            ? 'bg-red-500/15 text-red-300'
            : 'bg-emerald-500/15 text-emerald-300'
        }`}>
          {isLocked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
          {isLocked ? 'Locked' : 'Open'}
        </span>
        {cohort.maxMembers && (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${
            isFull ? 'bg-amber-500/15 text-amber-300' : 'bg-zinc-700/50 text-zinc-400'
          }`}>
            <Users className="w-3 h-3" />
            {cohort.studentCount}/{cohort.maxMembers} students
          </span>
        )}
        {isExpired && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-500/15 text-amber-300">
            <AlertCircle className="w-3 h-3" />
            Join window expired
          </span>
        )}
      </div>

      {/* Lock toggle */}
      <label className="flex items-center justify-between cursor-pointer group">
        <div>
          <span className="text-sm text-zinc-300 group-hover:text-white transition-colors">
            Lock enrollment
          </span>
          <p className="text-xs text-zinc-600 mt-0.5">
            Prevents new students from joining
          </p>
        </div>
        <button
          onClick={() => setIsLocked(!isLocked)}
          className={`relative w-11 h-6 rounded-full transition-colors ${
            isLocked ? 'bg-red-500/60' : 'bg-zinc-700'
          }`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${
            isLocked ? 'translate-x-5' : ''
          }`} />
        </button>
      </label>

      {/* Expiration date */}
      <div>
        <label className="text-sm text-zinc-300 flex items-center gap-2 mb-2">
          <Calendar className="w-3.5 h-3.5 text-zinc-500" />
          Join link expires
        </label>
        <div className="flex items-center gap-2">
          <input
            type="datetime-local"
            value={expiresAt}
            onChange={e => setExpiresAt(e.target.value)}
            className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-purple-500/50 [color-scheme:dark]"
          />
          {expiresAt && (
            <button
              onClick={() => setExpiresAt('')}
              className="p-2 text-zinc-500 hover:text-zinc-300 transition-colors"
              title="Clear expiration"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <p className="text-xs text-zinc-600 mt-1">
          {expiresAt
            ? `Link stops working after ${new Date(expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}`
            : 'No expiration set — link works indefinitely'}
        </p>
      </div>

      {/* Capacity */}
      <div>
        <label className="text-sm text-zinc-300 flex items-center gap-2 mb-2">
          <Hash className="w-3.5 h-3.5 text-zinc-500" />
          Student capacity
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={maxMembers}
            onChange={e => setMaxMembers(e.target.value)}
            placeholder="Unlimited"
            min="1"
            max="500"
            className="w-32 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-purple-500/50"
          />
          {maxMembers !== '' && (
            <button
              onClick={() => setMaxMembers('')}
              className="p-2 text-zinc-500 hover:text-zinc-300 transition-colors"
              title="Remove cap"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <p className="text-xs text-zinc-600 mt-1">
          {maxMembers
            ? `${cohort.studentCount} of ${maxMembers} spots filled (instructors don't count)`
            : 'No cap — unlimited students can join'}
        </p>
      </div>

      {/* Save button */}
      <button
        onClick={save}
        disabled={saving}
        className="w-full px-4 py-2 bg-amber-600 hover:bg-amber-500 disabled:bg-amber-600/50 text-white text-sm font-medium rounded-lg transition-colors"
      >
        {saving ? 'Saving...' : 'Save Controls'}
      </button>
    </div>
  );
}


// ========================================================================
// TAXONOMY EDITOR (inline panel)
// ========================================================================

function TaxonomyEditor({ cohort, onUpdate }) {
  const taxonomy = cohort.taxonomy || {};
  const [labels, setLabels] = useState(taxonomy.labels || []);
  const [newLabel, setNewLabel] = useState('');
  const [allowStudent, setAllowStudent] = useState(taxonomy.allow_student_additions !== false);
  const [saving, setSaving] = useState(false);

  const addLabel = () => {
    const trimmed = newLabel.trim();
    if (trimmed && !labels.includes(trimmed)) {
      setLabels(prev => [...prev, trimmed]);
      setNewLabel('');
    }
  };

  const removeLabel = (label) => {
    setLabels(prev => prev.filter(l => l !== label));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/cohorts/${cohort.slug}/taxonomy`, {
        enabled: true,
        labels,
        allow_student_additions: allowStudent,
      });
      onUpdate();
      toast.success('Taxonomy saved');
    } catch {
      toast.error('Failed to save taxonomy');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl space-y-4">
      <h3 className="text-sm font-medium text-white flex items-center gap-2">
        <Tag className="w-4 h-4 text-purple-400" />
        Taxonomy Labels
      </h3>

      {/* Existing labels */}
      <div className="flex flex-wrap gap-2">
        {labels.map(label => (
          <span
            key={label}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-purple-500/15 text-purple-300 text-xs font-medium"
          >
            {label}
            <button onClick={() => removeLabel(label)} className="text-purple-400 hover:text-red-400">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {labels.length === 0 && (
          <span className="text-xs text-zinc-500">No labels defined yet</span>
        )}
      </div>

      {/* Add new */}
      <div className="flex gap-2">
        <input
          type="text"
          value={newLabel}
          onChange={e => setNewLabel(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLabel(); } }}
          placeholder="Add a label (e.g. Residential)"
          maxLength={100}
          className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-purple-500/50"
        />
        <button
          onClick={addLabel}
          className="px-3 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm transition-colors"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Allow student additions toggle */}
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={allowStudent}
          onChange={e => setAllowStudent(e.target.checked)}
          className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-purple-500 focus:ring-purple-500/50"
        />
        <span className="text-sm text-zinc-300">Allow students to add their own labels</span>
      </label>

      <button
        onClick={save}
        disabled={saving}
        className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-purple-600/50 text-white text-sm font-medium rounded-lg transition-colors"
      >
        {saving ? 'Saving...' : 'Save Taxonomy'}
      </button>
    </div>
  );
}


// ========================================================================
// MAIN COMPONENT
// ========================================================================

export default function CohortDashboard() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [cohort, setCohort] = useState(null);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [myRole, setMyRole] = useState(null);

  // UI state
  const [viewMode, setViewMode] = useState('grid');
  const [activeFilter, setActiveFilter] = useState(null); // taxonomy label filter
  const [showSettings, setShowSettings] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // ── Cohort map state ───────────────────────────────────────────────
  // Fetched lazily: only when the user actually switches to map view.
  // A class with 200 submissions should not pay for the map payload on
  // every visit to the grid.
  const [mapData, setMapData] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState(null);

  // ── Create project, from inside the cohort ─────────────────────────
  // Before this existed the only path was: copy join link -> open
  // /submit?cohort=<slug> in a new page -> fill a DIFFERENT form. That
  // portal is built for a student arriving from OUTSIDE with a join
  // link. Someone already standing inside the class should not be sent
  // through it, and should not meet a second, unfamiliar form.
  //
  // So this reuses CreateProjectOverlay — the exact component the main
  // map uses — and does the cohort link afterwards.
  const [showCreate, setShowCreate] = useState(false);
  const [showMapLegend, setShowMapLegend] = useState(false);
  // Parents for a fork. Empty array = a brand new project.
  const [forkParents, setForkParents] = useState([]);

  // ── Project detail, opened in place ────────────────────────────────
  // Tiles used to navigate to /dashboard?project=<id> — the GLOBAL map.
  // That cannot work for a private cohort: those projects have
  // is_public=False and the global map filters on is_public==True, so
  // the deep link arrives at a map that does not contain the project
  // and the user just lands on the main map. Open the overlay here
  // instead and never leave the class.
  const [detailProject, setDetailProject] = useState(null);

  const openProject = useCallback((project) => {
    setDetailProject(project);
  }, []);

  // ── Map behaviour ──────────────────────────────────────────────────
  // The SAME controller the main map is built on. Clickable connection
  // lines, lineage highlighting, challenge selection and the scrubber all
  // come from here rather than being re-implemented per page — which is
  // exactly how this map ended up feeling different in the first place.
  //
  // Label filtering is applied to the projects BEFORE they reach the
  // controller, so picking "Wardrobe" narrows the constellation the same
  // way it narrows the grid. Connections are filtered to match: an edge
  // with a missing endpoint renders as nothing at all, silently.
  const mapProjects = useMemo(() => {
    const all = mapData?.projects || [];
    if (!activeFilter) return all;
    // cohortLabels is now the SAME shape here as in the grid — full label
    // objects, not bare strings. Previously the map endpoint returned
    // strings and the grid returned objects under the same field name,
    // so this filter and the one above it were written differently for
    // no reason a reader could see.
    return all.filter(p =>
      (p.cohortLabels || []).some(l => l.label === activeFilter)
    );
  }, [mapData, activeFilter]);

  const mapConnections = useMemo(() => {
    const visible = new Set(mapProjects.map(p => p.id));
    return (mapData?.connections || []).filter(
      c => visible.has(c.sourceId) && visible.has(c.targetId)
    );
  }, [mapData, mapProjects]);

  const mapChallenges = useMemo(() => {
    const visible = new Set(mapProjects.map(p => p.id));
    return (mapData?.challenges || []).filter(
      c => visible.has(c.project_id || c.projectId)
    );
  }, [mapData, mapProjects]);

  // ── Territory labels ───────────────────────────────────────────────
  // The cohort's own taxonomy, drawn on the map as ambient text over the
  // cluster of projects carrying each label — the same visual language as
  // the zone names, applied to the class's own vocabulary.
  //
  // This is what makes a cohort map read as THAT class's map without
  // repainting the constellation. Answers "let me see my taxonomy on the
  // map" with a pattern the map already speaks.
  const territoryLabels = useMemo(() => {
    const byLabel = new Map();
    for (const p of mapProjects) {
      for (const entry of (p.cohortLabels || [])) {
        const label = entry?.label;
        if (!label) continue;
        if (!byLabel.has(label)) byLabel.set(label, []);
        byLabel.get(label).push(p.id);
      }
    }
    return [...byLabel.entries()].map(([label, projectIds]) => ({ label, projectIds }));
  }, [mapProjects]);

  const map = useMapController({
    projects: mapProjects,
    connections: mapConnections,
    challenges: mapChallenges,
    isLoading: mapLoading,
    onOpenProject: (p) => setDetailProject(p),
  });

  // Fork from inside the class. Closes the detail overlay and opens the
  // create overlay in fork mode with this project pre-loaded as parent —
  // the same flow the main map runs, minus leaving the page.
  // Multi-parent fork: combine every selected node into one new project.
  // The class case this exists for — take a location, a wardrobe design
  // and a lighting test, and fork them together into a single shot.
  const forkSelected = useCallback((parents) => {
    if (!parents || parents.length === 0) return;
    setDetailProject(null);
    setForkParents(parents);
    setShowCreate(true);
  }, []);

  const forkProject = useCallback((project) => {
    setDetailProject(null);
    setForkParents([project]);
    setShowCreate(true);
  }, []);

  const closeCreate = useCallback(() => {
    setShowCreate(false);
    setForkParents([]);
  }, []);

  const isAdmin = myRole === 'admin';

  // ── Fetch everything ───────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [cohortRes, projectsRes, membersRes] = await Promise.all([
        api.get(`/cohorts/${slug}`),
        api.get(`/cohorts/${slug}/projects`).catch(() => ({ data: { projects: [] } })),
        api.get(`/cohorts/${slug}/members`).catch(() => ({ data: { members: [] } })),
      ]);

      setCohort(cohortRes.data);
      setProjects(projectsRes.data?.projects || []);
      setMembers(membersRes.data?.members || []);

      // Determine my role
      const me = (membersRes.data?.members || []).find(m => m.userId === user?.id);
      setMyRole(me?.role || null);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('You are not a member of this cohort.');
      } else if (err.response?.status === 404) {
        setError('Cohort not found.');
      } else {
        setError('Failed to load cohort.');
      }
    } finally {
      setLoading(false);
    }
  }, [slug, user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Fetch the cohort map ───────────────────────────────────────────
  // GET /api/cohorts/<slug>/map. Members only; the backend returns 404
  // for everyone else rather than 403, so as not to confirm that a
  // private cohort exists.
  //
  // This endpoint deliberately does NOT filter Project.is_public. Private
  // cohort projects have is_public=False (cohort_api.py sets it on add),
  // which is exactly why they cannot appear on the global map — and why
  // this view has to exist.
  const fetchMap = useCallback(async () => {
    setMapLoading(true);
    setMapError(null);
    try {
      const res = await api.get(`/cohorts/${slug}/map`);
      setMapData(res.data);
    } catch (err) {
      const status = err?.response?.status;
      setMapError(
        status === 404
          ? 'This map is not available. You may no longer be a member of this class.'
          : 'Could not load the map for this class.'
      );
      setMapData(null);
    } finally {
      setMapLoading(false);
    }
  }, [slug]);

  // Lazy: only fetch once the user actually opens map view, and refetch
  // when the label filter changes so the server does the filtering.
  useEffect(() => {
    if (viewMode === 'map') fetchMap();
  }, [viewMode, fetchMap]);

  // ── Copy join link ─────────────────────────────────────────────────
  // Two-step, the same sequence SubmitPage performs: create the project,
  // then link it to the cohort. The link call is what applies the
  // cohort's visibility — cohort_api.py sets is_public=False here when
  // the cohort is private. Skip it and the project stays public.
  const handleCreateSuccess = async (data) => {
    setShowCreate(false);
    setForkParents([]);

    const projectId = data?.id || data?.project?.id;
    if (!projectId) {
      toast.error('Project was created but could not be added to this class.');
      fetchData();
      return;
    }

    try {
      await api.post(`/cohorts/${slug}/projects`, { projectId, labels: [] });
      toast.success('Project added to this class');
    } catch {
      // The project exists — only the cohort link failed. Say so plainly
      // rather than implying the work was lost.
      toast.error('Project created, but adding it to this class failed. Try adding it from the project page.');
    }

    fetchData();
    if (viewMode === 'map') fetchMap();
  };

  const copyJoinLink = () => {
    // The code travels in the link so the common path is one click for a
    // student. It is NOT the only path — SubmitPage also accepts a typed
    // code, for the instructor who reads it out in class.
    //
    // joinCode is only present when the API decided this caller is a
    // cohort admin; for everyone else the field is absent and the link
    // falls back to slug-only, which will correctly prompt for a code.
    const code = cohort.joinCode;
    const url = code
      ? `${window.location.origin}/submit?cohort=${slug}&code=${encodeURIComponent(code)}`
      : `${window.location.origin}/submit?cohort=${slug}`;
    navigator.clipboard.writeText(url);
    setLinkCopied(true);
    toast.success(code ? 'Join link copied (includes the code)' : 'Join link copied!');
    setTimeout(() => setLinkCopied(false), 2000);
  };

  // ── Rotate the join code ───────────────────────────────────────────
  // A class code leaks by design: whiteboards, syllabi, forwarded emails,
  // screenshots. Rotation is what makes that survivable — existing members
  // keep their membership, the leaked code stops working.
  //
  // This is what a randomised slug could never give you. Rotating a slug
  // breaks every existing link and bookmark, so in practice nobody would,
  // and a leaked URL would be permanent.
  const [rotating, setRotating] = useState(false);
  const rotateJoinCode = async () => {
    if (!window.confirm(
      'Issue a new join code?\n\nThe current code stops working immediately. '
      + 'Students already in the class stay in, but any link you have shared '
      + 'will need to be sent again.'
    )) return;
    setRotating(true);
    try {
      await api.post(`/cohorts/${slug}/join-code`, {});
      await fetchData();
      toast.success('New join code issued');
    } catch {
      toast.error('Could not issue a new code');
    } finally {
      setRotating(false);
    }
  };

  // ── Remove member ──────────────────────────────────────────────────
  const removeMember = async (targetUserId) => {
    if (!window.confirm('Remove this member from the cohort?')) return;
    try {
      await api.delete(`/cohorts/${slug}/members/${targetUserId}`);
      setMembers(prev => prev.filter(m => m.userId !== targetUserId));
      toast.success('Member removed');
    } catch {
      toast.error('Failed to remove member');
    }
  };

  // ── Filter projects by taxonomy label ──────────────────────────────
  const filteredProjects = activeFilter
    ? projects.filter(p => (p.cohortLabels || []).some(l => l.label === activeFilter))
    : projects;

  // ── All unique labels across projects ──────────────────────────────
  const allLabels = [...new Set(
    projects.flatMap(p => (p.cohortLabels || []).map(l => l.label))
  )].sort();

  // ── Loading / error ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="text-center">
          <p className="text-zinc-400 mb-4">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-purple-400 hover:text-purple-300 text-sm"
          >
            Back to Map
          </button>
        </div>
      </div>
    );
  }

  if (!cohort) return null;

  const VisIcon = VISIBILITY_ICONS[cohort.visibility] || Globe;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-white">{cohort.name}</h1>
            {/* Enrollment status badge */}
            {cohort.isLocked && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-500/15 text-red-300">
                <Lock className="w-2.5 h-2.5" />
                Locked
              </span>
            )}
          </div>
          {cohort.description && (
            <p className="text-zinc-400 text-sm max-w-xl">{cohort.description}</p>
          )}
          <div className="flex items-center gap-4 mt-3 text-xs text-zinc-500">
            <span className="flex items-center gap-1">
              <Users className="w-3.5 h-3.5" />
              {cohort.maxMembers
                ? `${cohort.studentCount}/${cohort.maxMembers} students`
                : `${members.length} member${members.length !== 1 ? 's' : ''}`}
            </span>
            <span className="flex items-center gap-1">
              <Grid3X3 className="w-3.5 h-3.5" />
              {projects.length} project{projects.length !== 1 ? 's' : ''}
            </span>
            <span className="flex items-center gap-1">
              <VisIcon className="w-3.5 h-3.5" />
              {cohort.visibility}
            </span>
            {cohort.expiresAt && (
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {new Date(cohort.expiresAt) < new Date()
                  ? 'Expired'
                  : `Expires ${new Date(cohort.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Copy join link */}
          {isAdmin && (
            <button
              onClick={copyJoinLink}
              className="flex items-center gap-2 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-sm transition-colors"
            >
              {linkCopied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              {linkCopied ? 'Copied!' : 'Copy Join Link'}
            </button>
          )}

          {/* Members toggle */}
          <button
            onClick={() => setShowMembers(!showMembers)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
              showMembers ? 'bg-purple-500/20 text-purple-300' : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'
            }`}
          >
            <Users className="w-4 h-4" />
            Members
          </button>

          {/* Settings toggle (admin only) */}
          {isAdmin && (
            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                showSettings ? 'bg-purple-500/20 text-purple-300' : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'
              }`}
            >
              <Settings className="w-4 h-4" />
            </button>
          )}

          {/* New Project. Deliberately the FIRST action in this cluster:
              adding work is the primary thing anyone does here, and it
              used to be the hardest. */}
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">New Project</span>
          </button>

          {/* View toggle */}
          <div className="flex bg-zinc-800 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded-md transition-colors ${
                viewMode === 'grid' ? 'bg-zinc-700 text-white' : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              <Grid3X3 className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-md transition-colors ${
                viewMode === 'list' ? 'bg-zinc-700 text-white' : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              <List className="w-4 h-4" />
            </button>
            {/* Map view. For a PRIVATE cohort this is the only place its
                projects appear on a constellation at all — they are
                is_public=False and therefore absent from the global map. */}
            <button
              onClick={() => setViewMode('map')}
              title="Map view"
              className={`p-1.5 rounded-md transition-colors ${
                viewMode === 'map' ? 'bg-zinc-700 text-white' : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              <MapIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* ── Settings Panel (admin only) ────────────────────────────── */}
      {showSettings && isAdmin && (
        <>
          {/* Join code. Admin-only — the API omits joinCode entirely for
              anyone else, so this block simply has nothing to render for
              a student even if they somehow reached this panel. */}
          {cohort.joinCode && (
            <div className="mb-4 p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl">
              <h3 className="text-sm font-medium text-white flex items-center gap-2 mb-3">
                <Lock className="w-4 h-4 text-amber-400" />
                Join Code
              </h3>
              <div className="flex flex-wrap items-center gap-3">
                <code className="px-4 py-2.5 bg-zinc-950 border border-zinc-700 rounded-lg text-lg font-mono tracking-[0.2em] text-amber-300">
                  {cohort.joinCode}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(cohort.joinCode);
                    toast.success('Code copied');
                  }}
                  className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-sm transition-colors"
                >
                  Copy code
                </button>
                <button
                  onClick={rotateJoinCode}
                  disabled={rotating}
                  className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-zinc-400 rounded-lg text-sm transition-colors"
                >
                  {rotating ? 'Working...' : 'New code'}
                </button>
              </div>
              <p className="text-xs text-zinc-500 mt-3 leading-relaxed">
                Students need this to join. Without it, anyone who guessed
                the class URL could enrol themselves &mdash; the URL is built
                from the class name. Issue a new code if it gets out;
                current students keep their place.
              </p>
            </div>
          )}

          <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <InstructorControls cohort={cohort} onUpdate={fetchData} />
            <TaxonomyEditor cohort={cohort} onUpdate={fetchData} />
          </div>
        </>
      )}

      {/* ── Members Panel ──────────────────────────────────────────── */}
      {showMembers && (
        <div className="mb-6 p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl">
          <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-purple-400" />
            Members ({members.length})
            {cohort.maxMembers && (
              <span className="text-xs text-zinc-500 font-normal">
                · {cohort.studentCount} of {cohort.maxMembers} student spots filled
              </span>
            )}
          </h3>
          <div className="space-y-2">
            {members.map(m => (
              <div key={m.userId} className="flex items-center gap-3 py-2">
                <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-xs font-medium text-zinc-400 flex-shrink-0">
                  {(m.user?.username || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-white">{m.user?.username || 'Unknown'}</span>
                  {m.user?.firstName && (
                    <span className="text-xs text-zinc-500 ml-2">
                      {m.user.firstName} {m.user.lastName || ''}
                    </span>
                  )}
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                  m.role === 'admin'
                    ? 'bg-purple-500/15 text-purple-300'
                    : 'bg-zinc-800 text-zinc-500'
                }`}>
                  {m.role}
                </span>
                {isAdmin && m.userId !== user?.id && (
                  <button
                    onClick={() => removeMember(m.userId)}
                    className="text-zinc-600 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Filter bar ─────────────────────────────────────────────── */}
      {allLabels.length > 0 && (
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1">
          <button
            onClick={() => setActiveFilter(null)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
              !activeFilter
                ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600'
            }`}
          >
            All ({projects.length})
          </button>
          {allLabels.map(label => {
            const count = projects.filter(p =>
              (p.cohortLabels || []).some(l => l.label === label)
            ).length;
            return (
              <button
                key={label}
                onClick={() => setActiveFilter(activeFilter === label ? null : label)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  activeFilter === label
                    ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                    : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:border-zinc-600'
                }`}
              >
                {label} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* ── Projects ───────────────────────────────────────────────── */}
      {viewMode === 'map' ? (
        <div className="relative rounded-xl overflow-hidden border border-zinc-800 bg-zinc-950"
             style={{ height: 'calc(100vh - 320px)', minHeight: '420px' }}>
          {mapError ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center px-6">
                <AlertCircle className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-400 text-sm mb-3">{mapError}</p>
                <button
                  onClick={fetchMap}
                  className="text-purple-400 hover:text-purple-300 text-sm"
                >
                  Try again
                </button>
              </div>
            </div>
          ) : (
            <>
              <CosmicMap
                {...map.mapProps}
                territoryLabels={territoryLabels}
                surface="chalkboard"
                className="w-full h-full"
              />

              {/* The cross-zone payoff card — "a photo of morning light
                  inspired this light-sensing robot". useMapController has
                  always returned connectionInfo for every map; only the
                  main map rendered it, because the component was locked
                  inside Dashboard.jsx until it was extracted. */}
              {/* Zoom / reset / legend. Same cluster the main map uses —
                  shared component, not a copy, so the two cannot drift
                  into looking merely similar. */}
              <MapChrome
                onZoomIn={map.viewport.zoomIn}
                onZoomOut={map.viewport.zoomOut}
                onReset={map.viewport.resetView}
                onToggleLegend={() => setShowMapLegend(v => !v)}
                showLegend={showMapLegend}
                className="absolute bottom-4 right-4 z-20"
              />

              <MapLegend
                show={showMapLegend}
                onClose={() => setShowMapLegend(false)}
              />

              {map.connectionInfo && (
                <ConnectionInfoCard
                  source={map.connectionInfo.source}
                  target={map.connectionInfo.target}
                  onClose={map.clearConnectionInfo}
                  onViewProject={(project) => {
                    map.clearConnectionInfo();
                    setDetailProject(project);
                  }}
                />
              )}

              {/* Multi-select bar. A deliberately light stand-in for the
                  main map's SelectionTray, which also carries curated
                  branches, collections and journey — machinery the class
                  page does not have. This covers the one action that
                  matters here: fork several projects into one. */}
              {map.selection.isMultiSelectMode && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 px-4 py-2.5 rounded-xl bg-zinc-900/95 border border-zinc-700 backdrop-blur shadow-xl">
                  <span className="text-sm text-zinc-300">
                    {map.selection.multiSelectedIds.length} selected
                  </span>
                  <button
                    onClick={() => forkSelected(map.selection.multiSelectedProjects)}
                    disabled={map.selection.multiSelectedIds.length === 0}
                    className="px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 disabled:bg-purple-600/40 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors"
                  >
                    Fork {map.selection.multiSelectedIds.length > 1 ? 'these' : 'this'}
                  </button>
                  <button
                    onClick={() => map.selection.exitMultiSelect()}
                    className="text-zinc-500 hover:text-zinc-300 transition-colors"
                    title="Cancel"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Hint on first entering the mode. Shift-click is not
                  discoverable; saying so once costs nothing. */}
              {!map.selection.isMultiSelectMode && mapProjects.length > 1 && (
                <div className="absolute top-3 left-3 text-[11px] text-zinc-600 pointer-events-none">
                  Shift-click to select several projects
                </div>
              )}

              {/* Scrubber — appears when a connection line is plucked,
                  same as the main map. */}
              {map.scrubber.showScrubber && (
                <LineageScrubber
                  lineage={map.scrubber.scrubberLineage}
                  currentProjectId={map.mapProps.selectedProjectId}
                  onClose={map.scrubber.closeScrubber}
                  onViewProject={(p) => setDetailProject(p)}
                  onSelectProject={(p) => setDetailProject(p)}
                />
              )}

              {/* Empty state sits ON the map rather than replacing it, so
                  the zone labels stay visible and the space reads as a
                  map waiting for work — not as a broken page. */}
              {!mapLoading && (mapData?.projects || []).length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-center px-6 pointer-events-auto">
                    <Sparkles className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
                    <p className="text-zinc-500 mb-2">Nothing on this map yet</p>
                    {isAdmin && (
                      <button
                        onClick={copyJoinLink}
                        className="text-purple-400 hover:text-purple-300 text-sm"
                      >
                        Share the join link to get started
                      </button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className="text-center py-16">
          <Sparkles className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-500 mb-2">
            {projects.length === 0
              ? 'No projects submitted yet'
              : 'No projects match this filter'}
          </p>
          {isAdmin && projects.length === 0 && (
            <button
              onClick={copyJoinLink}
              className="text-purple-400 hover:text-purple-300 text-sm mt-2"
            >
              Share the join link with your students to get started
            </button>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredProjects.map(p => (
            <ProjectCard key={p.id} project={p} viewMode="grid" onOpen={openProject} />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredProjects.map(p => (
            <ProjectCard key={p.id} project={p} viewMode="list" onOpen={openProject} />
          ))}
        </div>
      )}

      {/* ── Project Detail overlay ─────────────────────────────────── */}
      {detailProject && (
        <OverlayErrorBoundary
          onClose={() => setDetailProject(null)}
          name="Project Detail"
        >
          <ProjectDetailOverlay
            project={detailProject}
            onClose={() => setDetailProject(null)}
            onFork={() => forkProject(detailProject)}
            onEdit={() => {
              // Editing lives on the main map's overlay stack. Sending the
              // user there is honest — but only works for projects the
              // global map can actually show.
              navigate(`/dashboard?project=${detailProject.id}&edit=1`);
            }}
          />
        </OverlayErrorBoundary>
      )}

      {/* ── Create Project overlay ─────────────────────────────────── */}
      {/* Same component the main map uses. Wrapped in an error boundary
          for the same reason Dashboard.jsx wraps it: the overlay loads
          async data and renders user content, and an unguarded throw
          here would take the whole cohort page down with it. */}
      {showCreate && (
        <OverlayErrorBoundary
          onClose={closeCreate}
          name="Create Project"
        >
          <CreateProjectOverlay
            mode={
              forkParents.length > 1 ? 'multifork'
              : forkParents.length === 1 ? 'fork'
              : 'new'
            }
            parentProjects={forkParents}
            onClose={closeCreate}
            onSuccess={handleCreateSuccess}
          />
        </OverlayErrorBoundary>
      )}
    </div>
  );
}